import { API_CONFIG } from '../config/constants';
import FingerprintService from './fingerprint.service'; // Changé ici - import par défaut

const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    if (!token) {
        throw new Error('No authentication token found');
    }
    return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
    };
};

export const projectService = {

    getAllProjects: async () => {
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/projects`, {
                method: 'GET',
                headers: getAuthHeader()
            });

            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                    throw new Error('Session expirée');
                }
                const error = await response.json();
                throw new Error(error.error || 'Échec de la récupération des projets');
            }

            const data = await response.json();
            return data.projects;
        } catch (error) {
            console.error('Error fetching projects:', error);
            if (error.message === 'No authentication token found') {
                window.location.href = '/login';
            }
            throw error;
        }
    },

    getLanguages: async () => {
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/languages`, {
                method: 'GET',
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Échec de la récupération des langages');
            }

            const data = await response.json();
            return data.languages;
        } catch (error) {
            console.error('Error fetching languages:', error);
            throw error;
        }
    },

    createProject: async (projectData) => {
        try {
            if (!projectData.name) {
                throw new Error('Le nom du projet est requis');
            }

            const fingerprint = await FingerprintService.getFingerprint();
            const token = localStorage.getItem('token');

            const formData = new FormData();
            formData.append('name', projectData.name);
            formData.append('fingerprint', fingerprint);

            // Ajouter chaque langue sélectionnée
            projectData.languages.forEach(lang => {
                formData.append('languages', lang);
            });

            // Ajouter les fichiers
            if (projectData.files) {
                projectData.files.forEach(file => {
                    formData.append('files', file);
                });
            }

            // Préparer les headers
            const headers = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${API_CONFIG.BASE_URL}/projects`, {
                method: 'POST',
                headers: headers,
                body: formData
            });

            if (!response.ok) {
                if (response.status === 401 && token) {
                    localStorage.removeItem('token');
                }
                const error = await response.json();

                if (response.status === 400 && error.required !== undefined) {
                    throw {
                        type: 'InsufficientCoins',
                        required: error.required,
                        current: error.current,
                        message: 'Solde de coins insuffisant'
                    };
                }

                throw new Error(error.error || 'Échec de la création du projet');
            }

            const responseData = await response.json();
            console.log('Réponse de l\'API createProject:', responseData);

            return {
                ...responseData,
                coinsInfo: {
                    used: responseData.coins_used,
                    remaining: responseData.remaining_coins
                }
            };

        } catch (error) {
            console.error('Error creating project:', error);
            throw error;
        }
    },

    getProjectStatus: async (groupId) => {
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/projects/${groupId}/status`, {
                method: 'GET',
                headers: getAuthHeader()
            });

            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                    throw new Error('Session expirée');
                }
                if (response.status === 404) {
                    throw new Error('Projet non trouvé');
                }
                const error = await response.json();
                throw new Error(error.error || 'Échec de la récupération du statut');
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching project status:', error);
            throw error;
        }
    },

    getSubtitles: async (projectId) => {
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/get_file/${projectId}/subtitles.json`, {
                method: 'GET',
                headers: getAuthHeader()
            });

            if (!response.ok) {
                // Si le fichier n'existe pas, générer les sous-titres
                if (response.status === 404) {
                    const generateResponse = await fetch(`${API_CONFIG.BASE_URL}/generate_subtitles/${projectId}`, {
                        method: 'POST',
                        headers: getAuthHeader()
                    });

                    if (!generateResponse.ok) {
                        throw new Error('Impossible de générer les sous-titres');
                    }

                    return await generateResponse.json();
                }

                const error = await response.json();
                throw new Error(error.error || 'Échec de la récupération des sous-titres');
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching subtitles:', error);
            throw error;
        }
    },

    updateSubtitleSettings: async (projectId, data) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            const headers = {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            };

            // Send the data directly
            const response = await fetch(`${API_CONFIG.BASE_URL}/update_subtitle_settings/${projectId}`, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(data)
            });

            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                    throw new Error('Session expirée');
                }

                if (response.status === 404) {
                    throw new Error('Projet non trouvé');
                }

                const error = await response.json();
                throw new Error(error.error || 'Échec de la mise à jour des paramètres de sous-titres');
            }

            return await response.json();
        } catch (error) {
            console.error('Error updating subtitle settings:', error);
            throw error;
        }
    },

    // Mettre à jour la méthode exportAudio pour inclure les sous-titres
    exportAudio: async (projectId, exportType = 'audio', includeSubtitles = false) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            // Utiliser le bon endpoint selon le type d'export
            const endpoint = exportType === 'video'
                ? `${API_CONFIG.BASE_URL}/export_audio_video/${projectId}`
                : `${API_CONFIG.BASE_URL}/export_audio/${projectId}`;

            // Toujours utiliser POST pour les exports vidéo
            const method = exportType === 'video' ? 'POST' : 'GET';

            // Préparer le corps de la requête
            const requestBody = {
                export_type: exportType,
                use_lipsync: true, // Toujours utiliser lipsync pour l'export audio
            };

            // Ajouter l'option des sous-titres pour la vidéo
            if (exportType === 'video') {
                requestBody.include_subtitles = includeSubtitles;
            }

            const body = method === 'POST' ? JSON.stringify(requestBody) : undefined;
            const contentType = method === 'POST' ? 'application/json' : undefined;

            const headers = {
                'Authorization': `Bearer ${token}`,
                ...(contentType && { 'Content-Type': contentType })
            };

            console.log(`Exporting ${exportType} for project ${projectId}...`);
            console.log(`Include subtitles: ${includeSubtitles}`);

            const response = await fetch(endpoint, {
                method,
                headers,
                body
            });

            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                    throw new Error('Session expirée');
                }
                if (response.status === 404) {
                    throw new Error('Projet non trouvé');
                }

                // Vérifier si la réponse est en JSON
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || `Échec de l'export ${exportType === 'video' ? 'vidéo' : 'audio'}`);
                } else {
                    // Gérer les réponses d'erreur non-JSON
                    const errorText = await response.text();
                    console.error('Server error response:', errorText);
                    throw new Error(`Échec de l'export ${exportType === 'video' ? 'vidéo' : 'audio'}`);
                }
            }

            // Récupérer le blob directement
            return await response.blob();
        } catch (error) {
            console.error(`Error exporting ${exportType}:`, error);

            // Fournir des informations d'erreur plus détaillées
            if (error.message && error.message.includes("'dict' object has no attribute 'sort'")) {
                throw new Error(`Erreur serveur pendant l'export. L'équipe technique a été notifiée. Veuillez réessayer plus tard.`);
            }

            if (error.message === 'No authentication token found') {
                window.location.href = '/login';
            }

            throw error;
        }
    },

    // Fonction utilitaire pour formater le temps en SRT
    formatSrtTime: (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);
        const ms = Math.floor((seconds - Math.floor(seconds)) * 1000);
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')},${ms.toString().padStart(3, '0')}`;
    },

    // Fonction utilitaire pour formater le temps en VTT
    formatVttTime: (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);
        const ms = Math.floor((seconds - Math.floor(seconds)) * 1000);
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
    },

    // Fonction pour télécharger un blob comme fichier
    downloadFile: (blob, filename) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    },

    // Check if video is available for a project
    checkVideoAvailability: async (projectId) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await fetch(`${API_CONFIG.BASE_URL}/get_file/${projectId}/video.mp4`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                if (response.status === 404) {
                    // Video not found - this is a normal scenario
                    return { available: false };
                }

                if (response.status === 401) {
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                    throw new Error('Session expirée');
                }

                const error = await response.json();
                throw new Error(error.error || "Échec de la vérification vidéo");
            }

            // If we get here, the video is available
            const data = await response.json();
            return {
                available: true,
                url: data.url
            };

        } catch (error) {
            // If it's a 404, just indicate video isn't available
            if (error.message && error.message.includes('404')) {
                return { available: false };
            }

            console.error('Error checking video availability:', error);
            if (error.message === 'No authentication token found') {
                window.location.href = '/login';
            }

            // For other errors, rethrow
            throw error;
        }
    },

    // Get a specific project group by ID (can be used to get language options)
    getProjectGroup: async (groupId) => {
        try {
            // Since there's no direct endpoint for this in your API,
            // we'll get all projects and filter by group ID
            const allProjects = await projectService.getAllProjects();
            const group = allProjects.find(g => g.group_id === groupId);

            if (!group) {
                throw new Error('Groupe de projets non trouvé');
            }

            return group;
        } catch (error) {
            console.error('Error fetching project group:', error);
            throw error;
        }
    },

    deleteProject: async (groupId) => {
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/projects/${groupId}`, {
                method: 'DELETE',
                headers: getAuthHeader()
            });

            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                    throw new Error('Session expirée');
                }
                if (response.status === 404) {
                    throw new Error('Groupe de projets non trouvé');
                }
                const error = await response.json();
                throw new Error(error.error || 'Échec de la suppression du groupe de projets');
            }

            return await response.json();
        } catch (error) {
            console.error('Error deleting project group:', error);
            throw error;
        }
    },

};